import React, { useMemo } from 'react';
import { NavLink, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useBi } from '../BiContext';
import './Sidebar.css';
import { avecPerimetre, useSynchroPerimetre, SANS_SOCIETE } from './perimetre';

// Sidebar persistante : l'arbre des rapports de la branche courante, visible
// sur TOUTES les pages — passer d'un rapport à l'autre = 1 clic, comme les
// pages Power BI ou les onglets Tableau. La branche se change en haut.
function Sidebar({ admin = false }) {
  const { data, branche, setBranche } = useBi();
  const navigate = useNavigate();
  // Les rapports codés (routes statiques r/vn-performance, r/am-vo-analyse…)
  // n'ont pas de paramètre :slug : on le lit dans l'URL pour que la branche
  // affichée suive aussi ces rapports (sinon Auto Métropole retombait sur Antilles).
  const params = useParams();
  const { pathname, search } = useLocation();
  const slug = params.slug || (pathname.match(/^\/r\/([^/]+)/) || [])[1];

  // Sur une page rapport, la branche affichée suit celle du rapport ouvert.
  const rapportCourant = slug ? data.rapports.find((r) => r.slug === slug) : null;
  const brancheAffichee = rapportCourant?.branche_code || branche;

  // Changer de plaque remet la concession groupée sur « Toutes ».
  const changerBranche = (code) => {
    setBranche(code);
    navigate(`/?branche=${code}`);
  };

  // ── Concession groupée (2026-10-27) ──
  // Les concessions groupées de la plaque (bi_societes.concession_master),
  // limitées aux sociétés de l'habilitation ; une société sans regroupement
  // compte pour elle-même. Le champ écrit le paramètre `societes` de la page.
  const pageAvecSocietes = !pathname.startsWith('/admin') && !(slug && SANS_SOCIETE.has(slug));
  useSynchroPerimetre(brancheAffichee, pageAvecSocietes);
  const concessions = useMemo(() => {
    const m = new Map();
    (data.societes || []).filter((x) => x.branche_code === brancheAffichee).forEach((x) => {
      const k = x.master || x.nom;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(x.code);
    });
    return [...m.entries()].map(([nom, codes]) => ({ nom, codes }));
  }, [data.societes, brancheAffichee]);
  const courant = new URLSearchParams(search).get('societes') || '';
  const codesCourants = courant && courant !== '__aucune__' ? courant.split(',').filter(Boolean).sort() : [];
  const choixCourant = !courant ? '' : (concessions.find((c) => c.codes.length === codesCourants.length && [...c.codes].sort().every((x, i) => x === codesCourants[i])) || {}).nom || '__perso';
  const nomsCourants = codesCourants.map((c) => (data.societes.find((x) => x.code === c) || {}).nom || c);
  const choisirConcession = (nom) => {
    const p = new URLSearchParams(search);
    const c = concessions.find((x) => x.nom === nom);
    if (!c) p.delete('societes'); else p.set('societes', c.codes.join(','));
    const q = p.toString();
    navigate({ pathname, search: q ? `?${q}` : '' }, { replace: true });
  };
  const lien = (path) => avecPerimetre(path, search);

  const rapportsBranche = data.rapports.filter(
    (r) => r.branche_code === brancheAffichee || r.branche_code === null
  );
  const sections = data.metiers
    .map((m) => ({ metier: m, rapports: rapportsBranche.filter((r) => r.metier_code === m.code) }))
    .filter((s) => s.rapports.length > 0);

  // Écrans d'administration liés à un bloc : visibles seulement avec ce bloc.
  const aBloc = (c) => data.metiers.some((m) => m.code === c);
  const objectifsOk = ['vn', 'vo', 'apv', 'pr'].some(aBloc);

  return (
    <nav className="bi-sidebar" aria-label="Rapports">
      <label className="bi-champ-label" htmlFor="bi-plaque">Plaque</label>
      <select
        id="bi-plaque"
        className="bi-branche-select"
        value={brancheAffichee || ''}
        onChange={(e) => changerBranche(e.target.value)}
        aria-label="Branche"
      >
        {data.branches.map((b) => (
          <option key={b.code} value={b.code}>{b.nom}</option>
        ))}
      </select>

      {concessions.length > 1 && (
        <div className="bi-perimetre">
          <label className="bi-champ-label" htmlFor="bi-concession">Concession groupée</label>
          <select
            id="bi-concession"
            className={`bi-branche-select bi-perimetre-select${choixCourant === '__perso' ? ' perso' : ''}`}
            value={choixCourant}
            onChange={(e) => choisirConcession(e.target.value)}
            disabled={!pageAvecSocietes}
            aria-label="Concession groupée"
            title={pageAvecSocietes ? 'Concession groupée : la sélection suit sur toutes les pages' : "Ce rapport ne se filtre pas par société : la sélection est conservée pour les autres pages"}
          >
            <option value="">Toutes les concessions</option>
            {concessions.map((c) => <option key={c.nom} value={c.nom}>{c.nom}</option>)}
            {choixCourant === '__perso' && <option value="__perso" disabled>Sélection personnalisée</option>}
          </select>
          {choixCourant === '__perso' && (
            <div className="bi-perimetre-detail" title={nomsCourants.join(', ')}>
              {courant === '__aucune__' ? 'aucune société' : nomsCourants.join(', ')}
            </div>
          )}
        </div>
      )}

      <NavLink to={lien(`/?branche=${brancheAffichee || ''}`)} className={() => `bi-nav-item accueil ${!slug ? 'active' : ''}`} end>
        Tous les rapports
        <span className="bi-nav-count">{rapportsBranche.length}</span>
      </NavLink>

      {sections.map(({ metier, rapports }) => (
        <div key={metier.code} className="bi-nav-group">
          <div className="bi-sidebar-label">{metier.nom}</div>
          {rapports.map((r) => (
            <NavLink
              key={r.slug}
              to={lien(`/r/${r.slug}`)}
              className={({ isActive }) => `bi-nav-item rapport ${isActive ? 'active' : ''}`}
            >
              {r.titre}
              {r.branche_code === null && <span className="bi-nav-toutes" title="Commun à toutes les branches">⊕</span>}
            </NavLink>
          ))}
        </div>
      ))}

      {/* Administration : hors de l'arbre des rapports, parce que ce n'en est
          pas un. Objectifs dans les deux branches auto ; le référentiel Marché
          ne concerne que les Antilles. */}
      {(admin || ((brancheAffichee === 'auto-antilles' || brancheAffichee === 'auto-metropole') && objectifsOk)) && (
        <div className="bi-nav-group">
          <div className="bi-sidebar-label">Administration</div>
          {(brancheAffichee === 'auto-antilles' || brancheAffichee === 'auto-metropole') && objectifsOk && (
            <NavLink to="/admin/objectifs"
              className={({ isActive }) => `bi-nav-item rapport ${isActive ? 'active' : ''}`}>
              Objectifs
            </NavLink>
          )}
          {brancheAffichee === 'auto-antilles' && aBloc('marche') && (
            <NavLink to="/admin/referentiel-marche"
              className={({ isActive }) => `bi-nav-item rapport ${isActive ? 'active' : ''}`}>
              Référentiel Marché
            </NavLink>
          )}
          {brancheAffichee === 'auto-antilles' && aBloc('apv') && (
            <NavLink to="/admin/ventes-additionnelles"
              className={({ isActive }) => `bi-nav-item rapport ${isActive ? 'active' : ''}`}>
              Ventes additionnelles
            </NavLink>
          )}
          {/* Sociétés (2026-10-29) : plaque et concession groupée, administrateurs. */}
          {admin && (
            <NavLink to="/admin/societes"
              className={({ isActive }) => `bi-nav-item rapport ${isActive ? 'active' : ''}`}>
              Sociétés
            </NavLink>
          )}
          {/* Accès (2026-10-07) : administrateurs seulement, toutes branches. */}
          {admin && (
            <NavLink to="/admin/acces"
              className={({ isActive }) => `bi-nav-item rapport ${isActive ? 'active' : ''}`}>
              Accès
            </NavLink>
          )}
        </div>
      )}
    </nav>
  );
}

export default Sidebar;
